import formatDecimal, {
  decimalRoundUp,
  isValidDecimal,
  roundUpToMultiplier,
  unformatDecimal,
} from '@paradigm/utils/formatDecimal';
import enhanceNegativeSign, {
  NEGATIVE_SIGN,
} from '@paradigm/utils/src/enhanceNegativeSign';
import {
  GrfqHedgeLeg,
  Grfq,
  GrfqLeg,
  GrfqRegularLeg,
} from '#/unified-rfqs/entities/grfq/domain';
import { BaseRfq } from '#/unified-rfqs/entities/shared';
import {
  getEffectiveMinBlockSize,
  getEffectiveMinContractIncrement,
  getEffectiveQuantityPrecision,
  getProduct,
  minBlockSizeIsQuoteNotional,
} from '#/products/domain';
import { isDatedInstrument } from '#/unified-rfqs/ui/rfqs/legs-functions';
import { findVenue, getVenue } from '#/unified-rfqs/entities/products/venue';
import { isStandardProduct, StandardProduct } from '#/products/types';

interface ExecConstraint {
  readonly minBlockSize: number;
  readonly minContractIncrement: number;
}

/**
 * Minimum entry quantity (coins/contracts) for a product.
 *
 * Where the venue denominates its block minimum in the quote currency
 * (Deribit's USDC-linear futures) the product quotes in coins, so the floor
 * is `minBlockSize / markPrice`. Everything else -- including every
 * coin-denominated linear perp on Paradex, Bullish and Bit.com -- keeps the
 * legacy `minBlockSize * contractSize`. Falls back to the raw minBlockSize
 * when a notional-denominated product has no mark price yet.
 */
export function minQuantityForProduct(
  product: StandardProduct,
  minBlockSize: number,
  markPrice: number | null,
): number {
  if (minBlockSizeIsQuoteNotional(product)) {
    return markPrice != null && markPrice > 0
      ? minBlockSize / markPrice
      : minBlockSize;
  }
  return minBlockSize * product.contractSize;
}

/**
 * Per-unit notional of a (possibly multi-leg) strategy: the USDC notional one
 * strategy unit trades across all legs, `sum(|ratio| * |leg mark price|)`.
 *
 * Used as the price in the notional-denominated min-size floor
 * (`minBlockSize / price`, today Deribit's USDC-linear futures). For a single
 * leg this is just that leg's mark; for a spread it sums each leg's
 * contribution. This is why the strategy mark (`bbo.mark_price`) must NOT be
 * used: a calendar spread's legs net it to ~0, which collapses the floor and
 * wrongly blocks the quote.
 */
export function strategyNotionalPrice(
  legs: readonly { ratio: number; markPrice: number }[],
): number {
  return legs.reduce(
    (acc, leg) => acc + Math.abs(leg.ratio) * Math.abs(leg.markPrice),
    0,
  );
}

export function normalizedMinBlockSize(
  productCode: string,
  legs: readonly GrfqLeg[],
): ExecConstraint {
  const product = getProduct(productCode);
  const { minBlockSizeCalculation } = getVenue(product.venue);

  // All products are now StandardProduct, so we can directly access minContractIncrement
  switch (minBlockSizeCalculation) {
    case 'leg':
      return normalizedMinBlockSizeByLeg(legs, product);
    case 'strategy':
      return normalizedMinBlockSizeByStrategy(legs, product.code);
    default:
      return {
        minBlockSize: product.minBlockSize,
        minContractIncrement: product.minContractIncrement,
      };
  }
}

function normalizedMinBlockSizeByStrategy(
  legs: readonly GrfqLeg[],
  productCode: string,
): ExecConstraint {
  const product = getProduct(productCode);
  const venue = getVenue(product.venue);
  const hedgeConstraint = getHedgeExecConstraint(
    legs.filter((leg) => leg.isHedge) as GrfqHedgeLeg[],
  );
  const nonHedgeConstraint = _getNonHedgeExecConstraint(
    legs.filter((leg) => !leg.isHedge) as GrfqRegularLeg[],
  );
  const smallestConstraint = minExecConstraint(
    nonHedgeConstraint,
    hedgeConstraint,
  );
  const boundedMinBlockSize = Math.max(
    smallestConstraint.minBlockSize,
    venue.normalizedMinBlockSize,
  );
  const minContractIncrement =
    product.kind === 'OPTION'
      ? product.minContractIncrement
      : smallestConstraint.minContractIncrement;
  const minBlockSize = roundUpToMultiplier(
    boundedMinBlockSize,
    minContractIncrement,
  );
  return { minBlockSize, minContractIncrement };
}

function normalizedMinBlockSizeByLeg(
  legs: readonly GrfqLeg[],
  product: StandardProduct,
): ExecConstraint {
  const resultMinBlockSize = legs.reduce((currentMinBlockSize, leg) => {
    const legProduct = getProduct(leg.product_code);
    if (!isStandardProduct(legProduct)) return 0;
    const legMinBlockSize = getEffectiveMinBlockSize(
      legProduct,
      isDatedInstrument(leg.instrument),
    );
    if (!leg.isHedge) return Math.max(currentMinBlockSize, legMinBlockSize);

    // A coin minimum is already in the strategy's own unit, and a hedge
    // leg does not tighten it: both 'leg' venues (Paradex, Bullish)
    // ignore legs.hedge in get_min_order_size, so folding a unit count
    // in here would invent a floor the API does not enforce -- and,
    // reading coins as a notional, one orders of magnitude too high.
    if (!minBlockSizeIsQuoteNotional(legProduct)) return currentMinBlockSize;

    // Notional minimum: how many strategy units reach it at this leg's
    // price. No 'leg' venue publishes one today.
    const parsedPrice = Number.parseFloat(unformatDecimal(leg.price!));
    const baseHedgeLegSize = parsedPrice / legProduct.contractSize;
    const unitsToReachMinBlockSize = Math.ceil(
      legMinBlockSize / baseHedgeLegSize,
    );
    return Math.max(currentMinBlockSize, unitsToReachMinBlockSize);
    // Seed with the perp floor; the loop Math.maxes in each leg's own
    // dated block size (the strategy leg included), so a dated override
    // is still applied.
  }, product.minBlockSize);
  // Take the increment from the strategy (non-hedge) leg so a dated
  // future uses its dated increment (Bullish is minBlockSizeCalculation:
  // 'leg', so this is the path a dated Bullish RFQ takes).
  const strategyLeg = legs.find((leg) => !leg.isHedge);
  const isDated =
    strategyLeg != null && isDatedInstrument(strategyLeg.instrument);
  return {
    minBlockSize: decimalRoundUp(
      resultMinBlockSize,
      getEffectiveQuantityPrecision(product, isDated),
    ),
    minContractIncrement: getEffectiveMinContractIncrement(product, isDated),
  };
}

// Can only be executed if leg products are standard (not mgp)
function _getNonHedgeExecConstraint(
  nonHedgeLegs: readonly GrfqRegularLeg[],
): ExecConstraint {
  const [firstLeg] = nonHedgeLegs;
  const product = getProduct(firstLeg!.product_code) as StandardProduct;
  const isDated = isDatedInstrument(firstLeg!.instrument);
  const minBlockSize = getEffectiveMinBlockSize(product, isDated);
  const legsRatiosSum = nonHedgeLegs.reduce(
    (total, leg) => (total += Math.abs(Number.parseFloat(leg.ratio))),
    0,
  );
  // Divide only where the venue's own get_min_order_size does
  // (minBlockSizePerStrategyUnit: Deribit alone), and never for a
  // notional minimum: its coin conversion in minQuantityForProduct
  // (notional / per-unit notional) already accounts for every leg, so
  // doing both puts a multi-leg spread below the real floor. Keying
  // this on the unit instead would divide Bybit's raw minimum, which
  // the API enforces whole.
  const perStrategyUnit =
    findVenue(product.venue)?.minBlockSizePerStrategyUnit === true &&
    !minBlockSizeIsQuoteNotional(product);
  return {
    minBlockSize: perStrategyUnit ? minBlockSize / legsRatiosSum : minBlockSize,
    minContractIncrement: getEffectiveMinContractIncrement(product, isDated),
  };
}

function getHedgeExecConstraint(
  hedgeLegs: readonly GrfqHedgeLeg[],
): ExecConstraint {
  return hedgeLegs.reduce<ExecConstraint>(_minHedgeExecConstraintReducer, {
    minBlockSize: Number.POSITIVE_INFINITY,
    minContractIncrement: Number.POSITIVE_INFINITY,
  });
}

// Can only be executed if leg products are standard (not mgp)
function _minHedgeExecConstraintReducer(
  current: ExecConstraint,
  leg: GrfqHedgeLeg,
): ExecConstraint {
  const legPrice = Number.parseFloat(leg.price);
  const legRatio = Number.parseFloat(leg.ratio);
  const product = getProduct(leg.product_code) as StandardProduct;
  const isDated = isDatedInstrument(leg.instrument);
  const minBlockSize = getEffectiveMinBlockSize(product, isDated);
  // Notional-denominated minimums: the coin floor is notional / price.
  // Coin-denominated ones keep minBlockSize * contractSize.
  const hedgeMinBlockSize = minBlockSizeIsQuoteNotional(product)
    ? minBlockSize / (legPrice * legRatio)
    : (minBlockSize * product.contractSize) / (legPrice * legRatio);

  const candidate: ExecConstraint = {
    minBlockSize: hedgeMinBlockSize,
    minContractIncrement: getEffectiveMinContractIncrement(product, isDated),
  };
  return minExecConstraint(current, candidate);
}

function minExecConstraint(
  cnstrA: ExecConstraint,
  cnstrB: ExecConstraint,
): ExecConstraint {
  if (cnstrA.minBlockSize === cnstrB.minBlockSize) {
    return cnstrA.minContractIncrement <= cnstrB.minContractIncrement
      ? cnstrA
      : cnstrB;
  }

  return cnstrA.minBlockSize < cnstrB.minBlockSize ? cnstrA : cnstrB;
}

export const CUSTOM_DESCRIPTION_PREFIX = 'Cstm';

export function isCustomDescription(text: string): boolean {
  return text.startsWith(CUSTOM_DESCRIPTION_PREFIX);
}

export function isCustomStrategy(rfq: BaseRfq) {
  return isCustomDescription(rfq.description);
}

export function isHedged(rfq: Grfq) {
  return rfq.legs.some((leg) => leg.isHedge);
}

/**
 * @param text Custom strategy description
 * @returns List of leg descriptions
 */
export function parseCustomDescription(text: string): readonly string[] {
  const [, legsText] = text.split(CUSTOM_DESCRIPTION_PREFIX);

  const dirtyLegs = legsText?.split('\n');
  const cleanLegs = dirtyLegs?.map(sanitizeLegs);

  if (cleanLegs == null)
    throw new TypeError(`Could not parse custom description "${text}"`);

  return cleanLegs;
}

/**
 *
 * @param text Full strategy description
 * @returns Each line of the strategy description, the first line is the
 *          strategy name, the following lines are hedge legs.
 */
export function parseStrategyDescription(text: string): readonly string[] {
  const dirtyLegs = text.split('\n');
  const cleanLegs = dirtyLegs.map(sanitizeLegs);

  return cleanLegs;
}

export function formatProductMark(
  markPrice: string | null,
  productCode: string | undefined,
) {
  if (productCode == null) {
    return NEGATIVE_SIGN;
  }

  return formatProductPrice(markPrice, productCode);
}

export function formatProductPrice(
  price: string | null,
  productCode?: string | undefined,
  customPrecision?: number | null,
) {
  if (price == null || !isValidDecimal(price)) {
    return NEGATIVE_SIGN;
  }

  const numeric = Number.parseFloat(price);
  const fixed =
    productCode != null
      ? numeric.toFixed(
          customPrecision ?? getProduct(productCode).pricePrecision,
        )
      : numeric.toString();

  const formatted = formatDecimal(fixed);
  const ehnancedNegative = enhanceNegativeSign(formatted);

  return ehnancedNegative;
}

function sanitizeLegs(text: string): string {
  return text.trim();
}
